import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ShoppingBasket, Truck, TrendingDown, PackageCheck, Copy, Send, ScanLine, Paperclip, ClipboardCheck, Camera } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import ChipEstado from '../ui/ChipEstado';
import ModalConfirmar from '../ui/ModalConfirmar';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import TiempoRelativo from '../ui/TiempoRelativo';
import { SelectFiltro } from '../ui/Filtros';
import Modal from '../ui/Modal';
import { Seccion, CampoValidado } from '../ui/Formulario';
import { useAvisos } from '../lib/contextoAvisos';
import { useEscanerTeclado } from '../lib/useEscanerTeclado';
import { pitidoOk, pitidoError } from '../lib/pitido';
import { uuidv7 } from '../lib/uuidv7';
import {
  registrarRecepcion, ordenesConRecepcionEnCola,
  recordarUbicaciones, ubicacionesRecordadas,
  recordarExigenciaDeLote, exigenciaDeLoteRecordada,
} from '../lib/colaRecepciones';
import { useDatosDeTrabajo } from '../lib/useDatosDeTrabajo';
import { loQueFaltaParaRecibir } from '../ventaja/recepcionLista';
import DondeAcomodarlo from '../inteligencia/DondeAcomodarlo';
import AvisoCopiaLocal from '../ui/AvisoCopiaLocal';
import ModalAsignar, { ChipAsignado } from '../ui/Asignar';
import CodigoQR from '../ui/CodigoQR';
import {
  MOTIVOS, nombreDeMotivo, exigeEvidencia, avisarDeLaFoto, faltantesDe,
  fotoDesdeArchivo, paraEnviar,
} from '../lib/evidencia';

const ESTADO_ORDEN = {
  BORRADOR: { etiqueta: 'Borrador', color: 'gris' },
  ENVIADA: { etiqueta: 'Confirmada', color: 'azul' },
  RECIBIDA_PARCIAL: { etiqueta: 'Recibida a medias', color: 'ambar' },
  RECIBIDA: { etiqueta: 'Recibida', color: 'verde' },
  CANCELADA: { etiqueta: 'Cancelada', color: 'gris' },
};

const dinero = (n) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' })
    .format(Number(n || 0));

export default function Compras({ contexto }) {
  // LLEGAR CON UNA ORDEN EN LA MANO. Hasta ahora este módulo ignoraba el
  // contexto: un aviso de «te toca recibir la orden 812» aterrizaba en
  // «Qué comprar», que ni siquiera es la pestaña de órdenes. Quien manda
  // una orden concreta quiere verla, así que se abre su pestaña y su
  // ficha.
  const [pestana, setPestana] = useState(
    contexto?.id_orden ? 'ordenes' : 'sugerencias',
  );
  useSubpantalla('pestana', pestana !== 'sugerencias', () => setPestana('sugerencias'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Compras</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Qué hay que pedir, a quién, y qué falta por llegar.
        </p>
      </div>

      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {[['sugerencias', 'Qué comprar'], ['ordenes', 'Órdenes'],
          ['proveedores', 'Proveedores'],
          ['cumplimiento', 'Cómo cumplen'],
          ['sin-constancia', 'Por registrar']].map(([id, et]) => (
          <button key={id} type="button" onClick={() => setPestana(id)} aria-current={pestana === id ? 'page' : undefined}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                    pestana === id
                      ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}>
            {et}
          </button>
        ))}
      </div>

      {pestana === 'sugerencias' && <PanelSugerencias />}
      {pestana === 'ordenes' && <PanelOrdenes idOrden={contexto?.id_orden} />}
      {pestana === 'proveedores' && <PanelProveedores />}
      {pestana === 'cumplimiento' && <PanelCumplimiento />}
      {pestana === 'sin-constancia' && <PanelSinConstancia />}
    </div>
  );
}

// ---------------------------------------------------------- sugerencias
function PanelSugerencias() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const cargar = useCallback(
    () => api.get('/compras/sugerencias')
      .then(setFilas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false)),
    [],
  );

  useEffect(() => {
    let vivo = true;
    api.get('/compras/sugerencias')
      .then((d) => vivo && setFilas(d))
      .catch((e) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, []);

  const sinProveedor = filas.filter((f) => !f.id_proveedor).length;

  const columnas = [
    { etiqueta: 'SKU', valor: (f) => f.sku, clase: 'font-mono text-xs' },
    { etiqueta: 'Producto', valor: (f) => f.producto, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Quedan', alineado: 'derecha', valor: (f) => Number(f.existencia),
      render: (f) => (
        <span className="tabular-nums text-red-700 font-semibold">
          {Number(f.existencia)}
        </span>
      ),
    },
    { etiqueta: 'Punto de reorden', alineado: 'derecha',
      valor: (f) => Number(f.punto_reorden) },
    { etiqueta: 'Salidas 30 d', alineado: 'derecha',
      valor: (f) => Number(f.consumo_30d) },
    {
      etiqueta: 'Pedir', alineado: 'derecha', valor: (f) => Number(f.sugerido),
      render: (f) => (
        <span className="tabular-nums font-semibold text-gray-900">
          {Number(f.sugerido)}
        </span>
      ),
    },
    {
      etiqueta: 'Proveedor', valor: (f) => f.proveedor || '',
      render: (f) => (f.proveedor
        ? <span className="text-gray-700">{f.proveedor}</span>
        : <span className="text-amber-700 text-xs font-semibold">Sin asignar</span>),
    },
    { etiqueta: 'Por qué', valor: (f) => f.explicacion, clase: 'text-xs text-gray-500' },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Qué hay que comprar"
        subtitulo="Productos por debajo de su punto de reorden. La cantidad ya viene redondeada al múltiplo del proveedor."
        nota={
          <Ayuda id="compras.cantidad_sugerida" />
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        claveFila={(f) => f.sku}
        exportar={{ nombre: 'sugerencias-compra', columnas }}
        filtrosExtra={
          <button
            onClick={async () => {
              setError(null);
              setGenerando(true);
              try {
                const ordenes = await api.post('/compras/ordenes/generar');
                setResultado(`${ordenes.length} borrador(es) creados.`);
                cargar();
              } catch (e) {
                setError(e.message);
              } finally {
                setGenerando(false);
              }
            }}
            disabled={generando || !filas.some((f) => f.id_proveedor)}
            className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-700 text-sm font-semibold px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-40"
          >
            <ShoppingBasket className="h-4 w-4" />
            {generando ? 'Generando…' : 'Generar borradores'}
          </button>
        }
        vacio={
          <EstadoVacio
            icono={PackageCheck}
            titulo="Nada por debajo del punto de reorden"
            descripcion="Cuando un producto baje de su punto de reorden aparecerá aquí, con cuánto conviene pedir."
          />
        }
      />

      {sinProveedor > 0 && (
        <div className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <strong>{sinProveedor} producto(s) sin proveedor asignado.</strong> No se
          pueden agrupar en una orden: meterlos en una sin destinatario sería
          inventarse a quién comprarles. Asígnales uno en la pestaña de
          Proveedores.
        </div>
      )}

      {resultado && (
        <div className="mt-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
          {resultado} Nacen en <strong>borrador</strong>: revísalos y confírmalos
          en la pestaña de Órdenes.
        </div>
      )}

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}
    </>
  );
}

// -------------------------------------------------------------- órdenes
function PanelOrdenes({ idOrden }) {
  const { tieneNivel } = useAuth();
  const avisos = useAvisos();
  const [referencia, setReferencia] = useState('');
  const [estado, setEstado] = useState('');
  const [accion, setAccion] = useState(null);
  // Órdenes con una recepción esperando señal dentro del teléfono. Sin
  // esto la lista enseña el pendiente de antes y parece que no se recibió
  // nada — que es exactamente lo que lleva a recibir la misma tarima dos
  // veces, con dos claves distintas que la idempotencia no puede atrapar.
  const [enCola, setEnCola] = useState({});
  const enColaRef = useRef({});
  const [errorContexto, setErrorContexto] = useState(null);

  // LA LISTA SE GUARDA EN EL TELÉFONO. Es la puerta de entrada a recibir:
  // sin ella no se puede ni empezar, y la cola de recepciones —que ya
  // aguanta el andén sin cobertura— no serviría de nada si para llegar a
  // ella hiciera falta señal.
  const {
    datos, error, cargando, momento, recargar: cargar,
  } = useDatosDeTrabajo(`/compras/ordenes${estado ? `?estado=${estado}` : ''}`);
  const filas = datos || [];

  // LA ORDEN SE PIDE APARTE, no se busca en la lista. La lista trae las
  // 50 últimas y puede llevar un filtro puesto: buscar ahí dentro haría
  // que el aviso funcionara para las órdenes recientes y no dijera nada
  // para las demás — un enlace que a veces va a ningún sitio es peor que
  // no tenerlo, porque nadie sabe cuándo fiarse.
  useEffect(() => {
    if (!idOrden) return;
    let vivo = true;
    api.get(`/compras/ordenes/${idOrden}`)
      .then((o) => vivo && setAccion({ tipo: 'ver', orden: o }))
      // Si ya no existe o no se puede ver, se dice: quedarse callado
      // parecería que el aviso estaba roto.
      .catch((e) => vivo && setErrorContexto(e.message));
    return () => { vivo = false; };
  }, [idOrden]);

  useEffect(() => {
    let vivo = true;
    const mirar = () => ordenesConRecepcionEnCola().then((m) => {
      if (!vivo) return;
      const antes = Object.keys(enColaRef.current).length;
      enColaRef.current = m;
      setEnCola(m);
      // Si la cola se vació, la mercancía ya entró y lo que muestra la
      // lista dejó de ser cierto: se recarga sola en vez de esperar a que
      // alguien sospeche.
      if (antes && !Object.keys(m).length) cargar();
    });
    mirar();
    const iv = setInterval(mirar, 5000);
    return () => { vivo = false; clearInterval(iv); };
  }, [cargar]);

  const columnas = [
    { etiqueta: 'Folio', valor: (o) => o.folio, clase: 'font-mono text-xs' },
    { etiqueta: 'Proveedor', valor: (o) => o.proveedor, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Estado', valor: (o) => o.estado,
      render: (o) => <ChipEstado mapa={ESTADO_ORDEN} clave={o.estado} />,
    },
    { etiqueta: 'Renglones', alineado: 'derecha', valor: (o) => o.renglones.length },
    {
      etiqueta: 'Pendiente', alineado: 'derecha',
      valor: (o) => o.renglones.reduce((s, r) => s + Number(r.pendiente), 0),
      render: (o) => {
        const p = o.renglones.reduce((s, r) => s + Number(r.pendiente), 0);
        return (
          <span className="inline-flex flex-col items-end">
            {p > 0
              ? <span className="tabular-nums text-amber-700">{p}</span>
              : <span className="text-gray-500">—</span>}
            {enCola[o.id] && (
              <span className="text-[11px] font-semibold text-red-700 leading-tight">
                recepción sin enviar
              </span>
            )}
          </span>
        );
      },
    },
    {
      etiqueta: 'Total', alineado: 'derecha', tipo: 'moneda', valor: (o) => Number(o.total_estimado),
      render: (o) => <span className="tabular-nums">{dinero(o.total_estimado)}</span>,
    },
    {
      etiqueta: 'A quién le toca', valor: (o) => o.asignado || 'sin asignar',
      render: (o) => (['ENVIADA', 'RECIBIDA_PARCIAL'].includes(o.estado)
        ? <ChipAsignado nombre={o.asignado} idAsignado={o.id_asignado} />
        // Una orden en borrador o ya recibida no es trabajo de nadie, y
        // decir «sin asignar» ahí invitaría a repartir lo que no existe.
        : <span className="text-gray-400">—</span>),
    },
    {
      // LA COLUMNA QUE FALTABA. «Confirmada» decía que la empresa se
      // comprometió a pagar, no que el proveedor se hubiera enterado. Un
      // almacén que espera mercancía que nadie pidió lo descubre el día
      // que no llega; aquí se ve antes.
      etiqueta: 'Envío', valor: (o) => textoEnvio(o),
      render: (o) => <EstadoEnvio orden={o} />,
    },
    { etiqueta: 'Origen', valor: (o) => (o.origen === 'REORDEN' ? 'Punto de reorden' : 'Manual'),
      clase: 'text-xs text-gray-500' },
    {
      etiqueta: 'Creada', tipo: 'fecha_hora', valor: (o) => o.creado_en,
      render: (o) => <TiempoRelativo valor={o.creado_en} />,
    },
  ];

  return (
    <>
      <div className="mb-3">
        <AvisoCopiaLocal momento={momento} que="Las órdenes" />
      </div>
      <ListaEstandar
        titulo="Órdenes de compra"
        subtitulo="Un borrador no compromete nada. Confirmarla es lo que le dice a la empresa que va a pagar."
        filtros={
          <SelectFiltro valor={estado} onChange={setEstado} vacio="Todos los estados"
                        opciones={Object.entries(ESTADO_ORDEN).map(([v, o]) => ({
                          valor: v, etiqueta: o.etiqueta,
                        }))} />
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'ordenes-compra', columnas }}
        vacio={
          <EstadoVacio
            icono={Truck}
            titulo="No hay órdenes"
            descripcion="Genera borradores desde «Qué comprar» o crea una a mano."
          />
        }
        acciones={(o) => [
          { etiqueta: 'Ver', onClick: () => setAccion({ tipo: 'ver', orden: o }) },
          ...(o.estado === 'BORRADOR' && tieneNivel('ADMIN')
            ? [{ etiqueta: 'Confirmar', onClick: () => setAccion({ tipo: 'enviar', orden: o }) }]
            : []),
          ...(o.estado === 'ENVIADA' && !o.acuse_en && tieneNivel('ENCARGADO')
            ? [{ etiqueta: 'Acuse', onClick: () => setAccion({ tipo: 'acuse', orden: o }) }]
            : []),
          ...(['ENVIADA', 'RECIBIDA_PARCIAL'].includes(o.estado) && tieneNivel('ENCARGADO')
            ? [{ etiqueta: 'Recibir', onClick: () => setAccion({ tipo: 'recibir', orden: o }) },
               { etiqueta: 'Asignar', onClick: () => setAccion({ tipo: 'asignar', orden: o }) }]
            : []),
          ...(!['RECIBIDA', 'CANCELADA'].includes(o.estado) && tieneNivel('ADMIN')
            ? [{ etiqueta: 'Cancelar', peligro: true,
                 onClick: () => setAccion({ tipo: 'cancelar', orden: o }) }]
            : []),
        ]}
      />

      {(error || errorContexto) && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {errorContexto
            ? `No se pudo abrir la orden del aviso: ${errorContexto}`
            : error}
        </div>
      )}

      {accion?.tipo === 'ver' && (
        <ModalOrden orden={accion.orden} onCerrar={() => setAccion(null)} />
      )}

      {accion?.tipo === 'asignar' && (
        <ModalAsignar
          ruta={`/compras/ordenes/${accion.orden.id}/asignar`}
          rolMinimo="ENCARGADO"
          que="la recepción"
          actual={accion.orden.id_asignado}
          nombreActual={accion.orden.asignado}
          onCerrar={() => setAccion(null)}
          onListo={() => { setAccion(null); cargar(); }}
        />
      )}

      {accion?.tipo === 'enviar' && (
        <ModalEnviarOrden
          orden={accion.orden}
          onCerrar={() => setAccion(null)}
          onListo={() => { setAccion(null); cargar(); }}
        />
      )}

      {accion?.tipo === 'acuse' && (
        <ModalConfirmar
          titulo={`Acuse de la orden ${accion.orden.folio}`}
          descripcion="El proveedor confirmó que la tiene. Es lo único que convierte «la mandamos» en «la recibieron»: son dos hechos distintos, y el segundo puede no llegar nunca."
          extra={
            <CampoValidado etiqueta="¿Cómo lo confirmó?" ayuda="Opcional, pero es lo que se consulta seis semanas después."
                           valor={referencia}>
              {(props) => (
                <input {...props} value={referencia}
                       onChange={(e) => setReferencia(e.target.value)}
                       placeholder="Correo de Laura del 5 de agosto, llamada al almacén…" />
              )}
            </CampoValidado>
          }
          etiquetaConfirmar="Registrar acuse"
          onConfirmar={async () => {
            await api.post(`/compras/ordenes/${accion.orden.id}/acuse`,
                           { referencia: referencia.trim() || null });
            avisos.exito(`Orden ${accion.orden.folio}: acuse registrado.`);
            setReferencia('');
            setAccion(null);
            cargar();
          }}
          onCerrar={() => { setReferencia(''); setAccion(null); }}
        />
      )}

      {accion?.tipo === 'cancelar' && (
        <ModalCancelar
          orden={accion.orden}
          onCerrar={() => setAccion(null)}
          onListo={() => { setAccion(null); cargar(); }}
        />
      )}

      {accion?.tipo === 'recibir' && (
        <ModalRecibir orden={accion.orden}
                      onCerrar={() => setAccion(null)}
                      onListo={() => { setAccion(null); cargar(); }} />
      )}
    </>
  );
}



// ------------------------------------------------------- cumplimiento
// EL DATO YA ESTABA Y NO LO MIRABA NADIE. N-14 empezó a registrar si la
// orden salió de aquí y si la acusaron; N-15 empezó a anotar el excedente
// en vez de rechazarlo, justamente para poder contestar «¿qué proveedores
// mandan de más?». Los dos dejaron el dato guardado y sin nadie que lo
// sumara — y un dato que no se mira no cambia ninguna decisión: se sigue
// comprando al mismo que entrega tarde porque nadie ha puesto los números
// uno al lado del otro.
function PanelCumplimiento() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [dias, setDias] = useState('180');
  const [error, setError] = useState(null);
  // EL METODO VIAJA CON EL NUMERO (Fase 3.2). Si este informe sale en un
  // archivo y llega a la bandeja del proveedor, tiene que decir contra
  // que se le midio — si no, es una nota que el no puede discutir.
  const [metodo, setMetodo] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/metodos/cumplimiento_proveedor')
      .then((m) => vivo && setMetodo(m))
      .catch(() => vivo && setMetodo(null));
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    let vivo = true;
    api.get(`/compras/informes/cumplimiento?dias=${dias}`)
      .then((r) => vivo && setFilas(r))
      .catch((e) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [dias]);

  // Marcar «cargando» lo hace quien cambia el periodo, no el efecto:
  // encadenar setState desde un efecto son dos renders y una regla del
  // linter, y aquí además esconde de dónde sale el cambio.
  function cambiarPeriodo(v) {
    setDias(v);
    setCargando(true);
  }

  const pct = (parte, total) => (total ? Math.round((parte / total) * 100) : null);

  const columnas = [
    { etiqueta: 'Proveedor', valor: (f) => f.proveedor, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Órdenes', alineado: 'derecha', valor: (f) => f.ordenes },
    {
      etiqueta: 'A tiempo', alineado: 'derecha',
      // Solo sobre las que TENÍAN fecha: exigir puntualidad a quien nunca
      // recibió una es inventarse el incumplimiento.
      valor: (f) => (f.con_fecha ? `${pct(f.a_tiempo, f.con_fecha)}%` : ''),
      render: (f) => (f.con_fecha === 0
        ? <span className="text-gray-500" title="Ninguna con fecha de entrega">—</span>
        : <span className={`tabular-nums font-semibold ${
            pct(f.a_tiempo, f.con_fecha) >= 80 ? 'text-green-700' : 'text-amber-700'}`}>
            {pct(f.a_tiempo, f.con_fecha)}%
            <span className="block text-[11px] font-normal text-gray-500">
              {f.a_tiempo} de {f.con_fecha}
            </span>
          </span>),
    },
    {
      etiqueta: 'Retraso medio', alineado: 'derecha',
      valor: (f) => (f.dias_retraso_medio == null ? '' : Number(f.dias_retraso_medio)),
      render: (f) => (f.dias_retraso_medio == null
        ? <span className="text-gray-500">—</span>
        : <span className="tabular-nums text-amber-700">
            {Number(f.dias_retraso_medio).toFixed(1)} d
          </span>),
    },
    {
      etiqueta: 'Mandó de más', alineado: 'derecha',
      valor: (f) => Number(f.unidades_de_mas),
      render: (f) => (Number(f.unidades_de_mas) > 0
        ? <span className="tabular-nums text-amber-700">
            +{Number(f.unidades_de_mas)}
            <span className="block text-[11px] font-normal text-gray-500">
              en {f.renglones_con_excedente} renglón(es)
            </span>
          </span>
        : <span className="text-gray-500">—</span>),
    },
    {
      etiqueta: 'Completas', alineado: 'derecha',
      valor: (f) => f.completas,
      render: (f) => (
        <span className="tabular-nums text-gray-700">
          {f.completas}
          {(f.parciales > 0 || f.canceladas > 0) && (
            <span className="block text-[11px] text-gray-500">
              {f.parciales > 0 && `${f.parciales} a medias`}
              {f.parciales > 0 && f.canceladas > 0 && ' · '}
              {f.canceladas > 0 && `${f.canceladas} cancelada(s)`}
            </span>
          )}
        </span>
      ),
    },
    {
      // LAS CANCELADAS QUE SÍ SON SUYAS, en columna aparte y nunca dentro
      // de la puntualidad: una cancelación no es una entrega tarde.
      //
      // Y SE ENSEÑA CUÁNTAS NO SE PUEDEN IMPUTAR. Las canceladas antes de
      // la migración 161 no tienen origen registrado; si son muchas, esta
      // columna todavía no dice gran cosa y quien la lee tiene derecho a
      // saberlo antes de usarla para decidir.
      etiqueta: 'Canceló él', alineado: 'derecha',
      valor: (f) => f.canceladas_imputables,
      render: (f) => (f.canceladas_imputables > 0
        ? <span className="tabular-nums font-semibold text-amber-700">
            {f.canceladas_imputables}
            {f.canceladas_sin_origen > 0 && (
              <span className="block text-[11px] font-normal text-gray-500">
                {f.canceladas_sin_origen} sin registrar
              </span>
            )}
          </span>
        : <span className="text-gray-500">
            —
            {f.canceladas_sin_origen > 0 && (
              <span className="block text-[11px] text-gray-500">
                {f.canceladas_sin_origen} sin registrar
              </span>
            )}
          </span>),
    },
    {
      etiqueta: 'Acusadas', alineado: 'derecha', valor: (f) => f.acusadas,
      clase: 'tabular-nums text-gray-600',
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Cómo cumplen los proveedores"
        subtitulo="Con lo que ya se guardaba en cada orden: puntualidad, exactitud y rastro del envío."
        nota={
          <NotaExplicativa titulo="Cómo se mide, y qué NO se le achaca al proveedor">
            La <strong>puntualidad</strong> se calcula solo sobre las órdenes que
            tenían fecha de entrega, y esa fecha sale del plazo que el propio
            proveedor declara. El{' '}
            <strong>retraso medio</strong> promedia solo las que llegaron tarde:
            mezclarlas con las adelantadas haría pasar por puntual a quien entrega
            la mitad con una semana de retraso. Las <strong>canceladas</strong> se
            cuentan aparte y no como incumplimiento: hoy no se registra quién
            pidió la cancelación.
            Y <strong>«sin constancia»</strong> no mide al proveedor: son órdenes
            confirmadas sin registro de envío.
          </NotaExplicativa>
        }
        filtros={
          <SelectFiltro valor={dias} onChange={cambiarPeriodo} etiqueta="Periodo"
                        opciones={[
                          { valor: '90', etiqueta: 'Últimos 3 meses' },
                          { valor: '180', etiqueta: 'Últimos 6 meses' },
                          { valor: '365', etiqueta: 'Último año' },
                        ]} />
        }
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'cumplimiento-proveedores', columnas,
                    pie: metodo?.al_calce }}
        claveFila={(f) => f.id_proveedor}
        vacio={
          <EstadoVacio
            icono={Truck}
            titulo="Todavía no hay con qué medir"
            descripcion="Aquí aparecen los proveedores en cuanto tengan órdenes confirmadas. Un borrador no cuenta: el proveedor ni lo ha visto."
          />
        }
      />
      {error && (
        <div role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}
    </>
  );
}

// ------------------------------------------------- envío de la orden
// Por dónde puede salir. El orden importa: los dos primeros son los que
// se usan de verdad en este mercado, y el resto existe para que nadie
// tenga que mentir eligiendo uno que no fue.
const METODOS = [
  { valor: 'WHATSAPP', etiqueta: 'WhatsApp' },
  { valor: 'CORREO', etiqueta: 'Correo electrónico' },
  { valor: 'TELEFONO', etiqueta: 'Por teléfono' },
  { valor: 'IMPRESA', etiqueta: 'Impresa / en mano' },
  { valor: 'PORTAL', etiqueta: 'Portal del proveedor' },
  { valor: 'OTRO', etiqueta: 'Otro' },
];

const ETIQUETA_METODO = Object.fromEntries(METODOS.map((m) => [m.valor, m.etiqueta]));

function textoEnvio(o) {
  if (o.estado === 'BORRADOR') return '';
  if (!o.enviado_metodo) return 'Sin constancia de envío';
  const base = ETIQUETA_METODO[o.enviado_metodo] || o.enviado_metodo;
  return o.acuse_en ? `${base} · acusada` : base;
}

// El hueco se ve. Una orden confirmada de la que no consta que haya
// salido no se pinta igual que una acusada por el proveedor: son estados
// distintos del mundo real y antes los dos decían «Confirmada».
function EstadoEnvio({ orden }) {
  if (orden.estado === 'BORRADOR') return <span className="text-gray-500">—</span>;
  if (!orden.enviado_metodo) {
    return (
      <span className="text-xs font-semibold text-amber-700">
        Sin constancia
      </span>
    );
  }
  return (
    <span className="text-xs text-gray-600">
      {ETIQUETA_METODO[orden.enviado_metodo] || orden.enviado_metodo}
      {orden.acuse_en && (
        <span className="block text-[11px] font-semibold text-green-700">
          Acusada por el proveedor
        </span>
      )}
    </span>
  );
}

// Confirmar la orden Y mandársela al proveedor, en la misma pantalla.
//
// LO QUE ESTO SUSTITUYE. Antes había un aviso que decía, literalmente,
// que confirmar «NO se la manda al proveedor: eso sigue siendo trabajo de
// una persona». Era honesto y era inútil: dejaba al usuario con una orden
// cerrada y la tarea de copiar los renglones a mano a un WhatsApp — que
// es el paso donde un 12 se convierte en un 21 y llega el triple de
// mercancía.
//
// El documento lo arma el SERVIDOR: dos sitios que formatean la misma
// orden acaban dando dos órdenes distintas.
function ModalEnviarOrden({ orden, onCerrar, onListo }) {
  const [doc, setDoc] = useState(null);
  const [metodo, setMetodo] = useState('WHATSAPP');
  const [destino, setDestino] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const avisos = useAvisos();

  useEffect(() => {
    api.get(`/compras/ordenes/${orden.id}/documento`)
      .then((d) => {
        setDoc(d);
        // Se rellena con lo que el proveedor tiene registrado, pero se
        // GUARDA lo que quede aquí: si se manda a otro correo, la orden
        // tiene que decir a cuál.
        setDestino(d.para_whatsapp || d.para_correo || '');
      })
      .catch((e) => setError(e.message));
  }, [orden.id]);

  // El destino sugerido lo cambia QUIEN cambia el método, no un efecto:
  // encadenar `setState` desde un efecto son dos renders y una regla del
  // linter, y aquí además esconde de dónde sale el valor.
  function cambiarMetodo(v) {
    setMetodo(v);
    if (!doc) return;
    setDestino(v === 'CORREO' ? (doc.para_correo || '')
      : v === 'WHATSAPP' || v === 'TELEFONO' ? (doc.para_whatsapp || '')
        : '');
  }

  const soloDigitos = (t) => (t || '').replace(/\D/g, '');
  const enlaceWhatsapp = doc && destino
    ? `https://wa.me/${soloDigitos(destino)}?text=${encodeURIComponent(doc.texto)}`
    : null;
  const enlaceCorreo = doc && destino
    ? `mailto:${destino}?subject=${encodeURIComponent(doc.asunto)}`
      + `&body=${encodeURIComponent(doc.texto)}`
    : null;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(doc.texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles (o sin HTTPS en desarrollo) queda el
      // textarea de abajo, que se puede seleccionar a mano.
      avisos.aviso('No se pudo copiar solo. Selecciona el texto de abajo.');
    }
  }

  async function confirmar() {
    setError(null);
    setEnviando(true);
    try {
      await api.post(`/compras/ordenes/${orden.id}/enviar`,
                     { metodo, enviado_a: destino.trim() || null });
      avisos.exito(`Orden ${orden.folio} confirmada y registrada como enviada `
        + `por ${ETIQUETA_METODO[metodo].toLowerCase()}.`);
      onListo();
    } catch (e) {
      setError(e.message);
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={`Enviar la orden ${orden.folio}`}
      subtitulo={`${orden.proveedor} · ${dinero(orden.total_estimado)}`}
      ancho="max-w-2xl"
      onCerrar={onCerrar}
      bloqueado={enviando}
      pie={
        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
          <button type="button" onClick={onCerrar} disabled={enviando}
                  className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            Cancelar
          </button>
          <button type="button" onClick={confirmar} disabled={enviando || !doc}
                  className="text-sm font-semibold px-3 py-2 rounded-lg text-white bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)] disabled:opacity-50">
            {enviando ? 'Confirmando…' : 'Confirmar y registrar el envío'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <NotaExplicativa titulo="Confirmar compromete el dinero; mandarla es otra cosa">
          Al confirmar, la empresa se compromete a pagar y el borrador deja de
          editarse. <strong>Mandársela al proveedor sigue siendo un acto tuyo</strong> —
          aquí tienes el documento listo—, y lo que se guarda es por dónde salió.
        </NotaExplicativa>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">
            {error}
          </div>
        )}

        {!doc && !error && <p role="status" className="text-sm text-gray-500">Preparando el documento…</p>}

        {doc && (
          <>
            <Seccion titulo="Cómo se la haces llegar">
              <CampoValidado etiqueta="Por dónde" valor={metodo}>
                {(props) => (
                  <select {...props} value={metodo} onChange={(e) => cambiarMetodo(e.target.value)}>
                    {METODOS.map((m) => (
                      <option key={m.valor} value={m.valor}>{m.etiqueta}</option>
                    ))}
                  </select>
                )}
              </CampoValidado>
              <CampoValidado etiqueta="A dónde" valor={destino}
                             ayuda="Se guarda tal cual: si mañana le cambian el correo al proveedor, la orden seguirá diciendo a dónde se mandó hoy.">
                {(props) => (
                  <input {...props} value={destino}
                         onChange={(e) => setDestino(e.target.value)}
                         placeholder="correo o teléfono" />
                )}
              </CampoValidado>
            </Seccion>

            <div>
              <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                <span className="text-sm font-semibold text-gray-900">El documento</span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={copiar}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    {copiado ? 'Copiado' : 'Copiar'}
                  </button>
                  {metodo === 'WHATSAPP' && enlaceWhatsapp && (
                    <a href={enlaceWhatsapp} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                      <Send className="h-3.5 w-3.5" aria-hidden="true" /> Abrir WhatsApp
                    </a>
                  )}
                  {metodo === 'CORREO' && enlaceCorreo && (
                    <a href={enlaceCorreo}
                       className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                      <Send className="h-3.5 w-3.5" aria-hidden="true" /> Abrir el correo
                    </a>
                  )}
                </div>
              </div>
              {/* De solo lectura y seleccionable: si el portapapeles falla
                  —sin HTTPS, o sin permiso— siempre queda copiarlo a mano. */}
              <textarea
                readOnly
                value={doc.texto}
                rows={12}
                aria-label="Texto de la orden de compra"
                className="entrada font-mono text-xs leading-relaxed"
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function ModalOrden({ orden, onCerrar }) {
  return (
    <Modal
      titulo={`Orden ${orden.folio}`}
      subtitulo={`${orden.proveedor}${orden.fecha_esperada ? ` · esperada el ${orden.fecha_esperada}` : ''}`}
      ancho="max-w-lg"
      onCerrar={onCerrar}
      derecha={<ChipEstado mapa={ESTADO_ORDEN} clave={orden.estado} />}
    >
      <div className="space-y-3">

        {/* LOOP MÓVIL F4 · M1/M3: la tabla se desplaza dentro de su
            propio contenedor, no la página, cuando no cabe en el teléfono. */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm border-y border-gray-200">
          <thead>
            <tr className="text-[11px] uppercase text-gray-500">
              <th className="text-left py-1">Producto</th>
              <th className="text-right py-1">Pedido</th>
              <th className="text-right py-1">Recibido</th>
              <th className="text-right py-1">Importe</th>
            </tr>
          </thead>
          <tbody>
            {orden.renglones.map((r) => (
              <tr key={r.sku} className="border-t border-gray-100">
                <td className="py-1.5">
                  <div className="text-gray-800">{r.producto}</div>
                  <div className="text-xs font-mono text-gray-500">{r.sku}</div>
                </td>
                <td className="py-1.5 text-right tabular-nums">{Number(r.cantidad)}</td>
                <td className="py-1.5 text-right tabular-nums">
                  <span className={Number(r.pendiente) > 0 ? 'text-amber-700' : ''}>
                    {Number(r.cantidad_recibida)}
                  </span>
                  {/* Lo que el proveedor mandó de más. Antes se rechazaba, así
                      que no quedaba en ninguna parte y nadie podía saber qué
                      proveedores mandan de más. */}
                  {Number(r.cantidad_excedente) > 0 && (
                    <span className="block text-[11px] font-semibold text-amber-700">
                      +{Number(r.cantidad_excedente)} de más
                    </span>
                  )}
                </td>
                <td className="py-1.5 text-right tabular-nums">{dinero(r.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Total estimado</span>
          <span className="font-semibold tabular-nums">{dinero(orden.total_estimado)}</span>
        </div>

        {orden.nota && <p className="text-sm text-gray-600">{orden.nota}</p>}
        {orden.motivo_cancelacion && (
          <div className="text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-700">
            Cancelada: {orden.motivo_cancelacion}
          </div>
        )}

        <button type="button" onClick={onCerrar}
                className="w-full border border-gray-300 text-gray-700 font-semibold py-2 rounded-lg hover:bg-gray-50">
          Cerrar
        </button>
      </div>
    </Modal>
  );
}

function ModalRecibir({ orden, onCerrar, onListo }) {
  const pendientes = orden.renglones.filter((r) => Number(r.pendiente) > 0);
  // A CIEGAS SE EMPIEZA EN BLANCO. Prefijar la cantidad esperada es
  // exactamente el sesgo que la recepción a ciegas viene a quitar: quien
  // recibe ve un 100 puesto y confirma un 100 sin contar.
  const [aCiegas, setACiegas] = useState(false);
  const [cantidades, setCantidades] = useState(
    () => Object.fromEntries(pendientes.map((r) => [r.sku, String(Number(r.pendiente))])),
  );
  const [ubicacion, setUbicacion] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);
  // CAL-2 · lo de calidad, por renglón: lote nuestro, lote del proveedor
  // y certificado. Un solo objeto por SKU para que añadir un cuarto dato
  // en una fase siguiente no sea otro `useState` más.
  const [calidad, setCalidad] = useState({});
  // ¿Esta empresa exige el lote del proveedor? Nace en lo último que se
  // supo, no en `false`: ver `exigenciaDeLoteRecordada`.
  const [exigeLote, setExigeLote] = useState(() => exigenciaDeLoteRecordada());
  const [subiendo, setSubiendo] = useState(null);
  const [ultimo, setUltimo] = useState(null);   // último escaneo, para verlo
  const [editando, setEditando] = useState(false);
  // ¿Se empezó a contar escaneando? Ver el porqué en el primer escaneo.
  //
  // Va en estado Y en referencia: el estado es para pintar, y la
  // referencia es para DECIDIR. Con dos escaneos seguidos —que es lo
  // normal con un lector— los dos callbacks verían el mismo `contando`
  // todavía en falso, y el segundo volvería a poner todo a cero borrando
  // lo que acababa de contar el primero.
  const [contando, setContando] = useState(false);
  const contandoRef = useRef(false);
  // UNA CLAVE POR RECEPCIÓN, no por envío. Se genera al abrir el diálogo y
  // no cambia: si el botón se pulsa dos veces, o si la primera petición se
  // pierde de vuelta y se reintenta, el servidor reconoce que es la misma
  // recepción y no mete la mercancía otra vez. Sin esto, recibir dos veces
  // deja existencia duplicada que no se descubre hasta el siguiente conteo.
  const claveRef = useRef(null);
  if (claveRef.current == null) { claveRef.current = uuidv7(); }
  // ¿Esta orden ya tiene una recepción esperando señal? Es el único aviso
  // que puede evitar el error caro: la idempotencia protege del reenvío de
  // la MISMA recepción, no de que alguien cuente la tarima otra vez y
  // mande una segunda con clave nueva.
  const [yaEnCola, setYaEnCola] = useState(0);

  // --- Fase 4 · el acuse y la evidencia -----------------------------
  // TODO OPCIONAL SALVO LA FOTO CUANDO HAY ALGO QUE ENSEÑAR. Una
  // recepción sin acuse se registra igual: lo que cambia es que queda
  // constando que no lo hubo.
  const [entregadoPor, setEntregadoPor] = useState('');
  const [acuseToken, setAcuseToken] = useState('');
  const [huboDano, setHuboDano] = useState(false);
  const [huboRechazo, setHuboRechazo] = useState(false);
  const [fotos, setFotos] = useState([]);
  // EN CONTEO A CIEGAS, EL HUECO NO SE ENSEÑA HASTA QUE SE INTENTA
  // GUARDAR. Decir «faltan 40 de MAT-1» mientras alguien cuenta es
  // enseñarle lo esperado, que es exactamente lo que el conteo a ciegas
  // viene a tapar: bastaría con subir el número hasta que el aviso rojo
  // desapareciera. Cuando ya se tecleó todo y se pulsa Recibir, la
  // reconciliación se iba a decir de todas formas.
  const [revelado, setRevelado] = useState(false);
  const avisos = useAvisos();

  useEffect(() => {
    let vivo = true;
    ordenesConRecepcionEnCola()
      .then((m) => vivo && setYaEnCola(m[orden.id] || 0));
    return () => { vivo = false; };
  }, [orden.id]);

  useEffect(() => {
    // Sin señal esta llamada falla y la lista llegaría vacía: no habría
    // dónde meter la mercancía y no se podría ni confirmar. Se usa la
    // última lista vista, que es lo que hace que recibir sin cobertura
    // sea posible en vez de solo estar encolado a medias.
    const usar = (u) => {
      setUbicaciones(u);
      if (u.length) setUbicacion((v) => v || u[0].codigo);
    };
    api.get('/ubicaciones')
      .then((u) => { recordarUbicaciones(u); usar(u); })
      .catch(() => usar(ubicacionesRecordadas()));

    // Sin señal se queda con lo último que se supo, que es lo correcto:
    // caer en «no lo exige» encolaría una recepción que el servidor va a
    // rechazar al recuperar la red, por algo que la pantalla nunca dijo.
    api.get('/calidad/configuracion')
      .then((c) => {
        recordarExigenciaDeLote(c.exigir_lote_proveedor);
        setExigeLote(c.exigir_lote_proveedor);
      })
      .catch(() => { /* se queda con lo recordado */ });
  }, []);

  // RECIBIR ESCANEANDO, que es como se recibe de verdad.
  //
  // Teclear renglón por renglón una entrega de cuarenta referencias es
  // lento y es justo donde se cambia un 12 por un 21. El almacén ya tiene
  // lector: aquí se usa.
  //
  // EL LECTOR SE APAGA MIENTRAS SE EDITA A MANO. Un lector es un teclado:
  // si alguien tiene el cursor en una cantidad y escanea, los dígitos
  // entran EN ESE CAMPO además de contarse — y la corrupción es
  // silenciosa. Con el foco en un campo de cantidad, el escáner no
  // escucha; al salir, vuelve.
  useEscanerTeclado(
    (codigo) => {
      api.get(`/qr/resolver?codigo=${encodeURIComponent(codigo)}`)
        .then((r) => {
          if (r.tipo !== 'PRODUCTO') {
            pitidoError();
            avisos.aviso(`El código ${codigo} no es un producto.`);
            return;
          }
          const sku = r.datos.sku;
          // La caja de doce mete doce. El resolutor ya lo dice; adivinarlo
          // aquí sería contar de uno en uno una tarima entera.
          const unidades = Number(r.presentacion?.unidades || 1);

          const renglon = pendientes.find((x) => x.sku === sku);
          if (!renglon) {
            // Llegó algo que no se pidió. Es un hallazgo, no un descuido
            // del que escanea: el backend tampoco lo acepta contra esta
            // orden porque no hay renglón contra el que reconciliarlo.
            pitidoError();
            avisos.aviso(
              `${sku} no está en esta orden. Si de verdad llegó, regístralo `
              + 'como entrada aparte: aquí no hay renglón contra el que cuadrarlo.',
            );
            setUltimo({ sku, unidades, error: true });
            return;
          }

          // EL PRIMER ESCANEO EMPIEZA LA CUENTA DESDE CERO.
          //
          // Sin recepción a ciegas los campos vienen rellenos con lo que
          // falta —«confirma lo esperado»—, y sumar el escaneo encima
          // daría 101 de 100 a la primera pieza. Escanear ES contar: en
          // cuanto alguien pasa la primera pieza, lo que valía era el
          // conteo, no la suposición. Se hace una sola vez y se dice.
          const primera = !contandoRef.current;
          contandoRef.current = true;
          setCantidades((c) => {
            const base = primera
              ? Object.fromEntries(pendientes.map((x) => [x.sku, '']))
              : c;
            const actual = Number(base[sku] || 0);
            return { ...base, [sku]: String(actual + unidades) };
          });
          setContando(true);
          pitidoOk();
          setUltimo({ sku, unidades, producto: renglon.producto, error: false });
        })
        .catch(() => {
          pitidoError();
          avisos.aviso(`No se reconoce el código ${codigo}.`);
          setUltimo({ sku: codigo, error: true });
        });
    },
    { activo: !editando },
  );

  function ponerCalidad(sku, campo, valor) {
    setCalidad((c) => ({ ...c, [sku]: { ...(c[sku] || {}), [campo]: valor } }));
  }

  async function subirCertificado(sku, archivo) {
    if (!archivo) return;
    setSubiendo(sku);
    try {
      const r = await api.subir('/calidad/adjuntos', archivo);
      setCalidad((c) => ({
        ...c,
        [sku]: { ...(c[sku] || {}), id_certificado: r.id, certificado: r.nombre },
      }));
      avisos.exito(`Certificado adjuntado a ${sku}.`);
    } catch (e) {
      // SE DICE Y NO SE ROMPE. El certificado es opcional: que falle su
      // subida no puede impedir meter al almacén una mercancía que ya
      // está en el andén. Sin señal, se recibe y se adjunta después.
      avisos.aviso(
        `No se pudo adjuntar el certificado de ${sku}: `
        + `${e instanceof ErrorApi ? e.message : 'sin conexión'}. `
        + 'Puedes recibir igual y adjuntarlo después desde Rastrear.',
      );
    } finally {
      setSubiendo(null);
    }
  }

  function cambiarModo(activo) {
    setACiegas(activo);
    setContando(false);
    contandoRef.current = false;
    setUltimo(null);
    setCantidades(activo
      ? Object.fromEntries(pendientes.map((r) => [r.sku, '']))
      : Object.fromEntries(pendientes.map((r) => [r.sku, String(Number(r.pendiente))])));
  }

  const esCuarentena = ubicaciones.find((u) => u.codigo === ubicacion)?.tipo === 'CUARENTENA';

  // LO QUE VA A VIAJAR, calculado una vez y usado en los dos sitios: para
  // pintar el aviso mientras se captura, y para armar la petición. Si
  // fueran dos cuentas distintas, el día que se separaran la pantalla
  // diría que se puede guardar y el servidor diría que no.
  const renglonesVivos = pendientes
    .map((r) => ({
      sku: r.sku,
      cantidad: Number(cantidades[r.sku] || 0),
      // CUÁNTO SE ESPERABA, que es lo que permite al servidor saber que
      // hubo faltante sin volver a consultar la orden. Va siempre: si se
      // mandara solo a veces, la regla de la foto la decidiría la
      // pantalla, y una regla que la pantalla puede apagar no es una
      // regla.
      cantidad_pedida: Number(r.pendiente),
    }))
    .filter((r) => r.cantidad > 0);
  const faltantes = faltantesDe(renglonesVivos);
  const necesitaFoto = exigeEvidencia({
    hayFaltante: faltantes.length > 0, huboDano, huboRechazo,
  });
  // QUÉ SE PUEDE DECIR MIENTRAS SE CUENTA A CIEGAS, Y QUÉ NO: está
  // entero en `avisarDeLaFoto`, con su porqué.
  const avisar = avisarDeLaFoto({
    faltantes, huboDano, huboRechazo, aCiegas, revelado,
  });
  // LO QUE FALTA PARA PODER RECIBIR, calculado mientras se captura y no
  // al pulsar. Las mismas reglas que `onConfirmar` comprueba abajo, en
  // una función sin React para que se puedan probar solas — el porqué
  // está entero en `ventaja/recepcionLista.js`.
  //
  // `puedeDecirLoDeLaFoto` es `avisar` y no un cálculo nuevo: contar a
  // ciegas oculta lo que debería llegar, y decir aquí «falta la foto del
  // faltante» sería contarlo por la puerta de atrás.
  const estorbos = loQueFaltaParaRecibir({
    pendientes, cantidades, calidad, ubicacion, exigeLote,
    necesitaFoto, puedeDecirLoDeLaFoto: avisar, fotos,
  });

  return (
    <ModalConfirmar
      // RECEPCIÓN A MEDIAS · TC-DR-AT. Con cantidades tocadas o fotos tomadas,
      // atrás pregunta antes de cerrarla: las fotos no se guardan en otro sitio.
      guardia={() => fotos.length > 0}
      titulo={`Recibir la orden ${orden.folio}`}
      descripcion="Lo que se reciba entra al inventario de verdad, con su costo. Puedes recibir solo una parte."
      extra={
        <div className="space-y-3">
          {yaEnCola > 0 && (
            <div role="alert" className="rounded-lg border border-red-300 bg-red-50 p-2.5 text-sm">
              <span className="block font-semibold text-red-800">
                Esta orden ya tiene {yaEnCola} recepción(es) guardada(s) sin enviar
              </span>
              <span className="block text-xs text-red-700 mt-0.5">
                Lo que ves abajo como pendiente NO las descuenta todavía. Si
                vuelves a contar la misma mercancía, entrará dos veces al
                recuperar señal.
              </span>
            </div>
          )}
          <label className="flex items-start gap-3">
            <input type="checkbox" checked={aCiegas}
                   onChange={(e) => cambiarModo(e.target.checked)}
                   className="mt-1" />
            <span>
              <span className="block text-sm font-semibold text-gray-900">
                Contar a ciegas
              </span>
              <span className="block text-xs text-gray-500">
                Oculta lo que <em>debería</em> llegar y empieza en blanco. Cuenta lo
                que hay; el sistema compara después y anota la diferencia contra
                esta orden.
              </span>
            </span>
          </label>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              Dónde entra la mercancía
            </span>
            <select value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}
                    className="entrada">
              {ubicaciones.map((u) => (
                <option key={u.id} value={u.codigo}>
                  {u.codigo} — {u.nombre}{u.tipo === 'CUARENTENA' ? ' (cuarentena)' : ''}
                </option>
              ))}
            </select>
            {esCuarentena && (
              <span className="block text-xs text-amber-700 mt-1 font-semibold">
                Queda retenida: de una cuarentena no se vende. Se libera con un
                traspaso a su ubicación definitiva cuando pase la revisión.
              </span>
            )}
          </label>

          {/* QUE SE VEA QUE ESTA ESCUCHANDO. Un escáner que funciona sin
              decirlo obliga a comprobar en la lista después de cada pieza,
              que es justo lo que se quería evitar. */}
          <div className={`rounded-lg border p-2.5 text-sm ${
            editando
              ? 'border-gray-200 bg-gray-50 text-gray-500'
              : 'border-[var(--color-marca)] bg-[var(--color-marca-suave)] text-gray-700'}`}>
            <div className="flex items-center gap-2">
              <ScanLine className={`h-4 w-4 shrink-0 ${
                editando ? 'text-gray-400' : 'text-[var(--color-marca)]'}`} aria-hidden="true" />
              <span className="font-semibold">
                {editando
                  ? 'Lector en pausa mientras escribes'
                  : 'Escanea las piezas: se van sumando solas'}
              </span>
            </div>
            {/* `aria-live` para que un lector de pantalla lo cante: aquí
                el pitido no le sirve a quien no oye. */}
            <div aria-live="polite" className="mt-1 text-xs">
              {contando && !aCiegas && (
                <span className="block text-amber-700 font-semibold mb-0.5">
                  Contando desde cero: escanear es contar, y lo que
                  había puesto dejó de valer.
                </span>
              )}
              {ultimo ? (
                ultimo.error
                  ? <span className="font-semibold text-red-700">
                      {ultimo.sku} — no entra en esta orden
                    </span>
                  : <span className="text-gray-600">
                      Último: <strong>+{ultimo.unidades}</strong> de {ultimo.sku}
                      {ultimo.producto ? ` · ${ultimo.producto}` : ''}
                    </span>
              ) : (
                <span className="text-gray-500">
                  Nada escaneado todavía. También puedes teclear las cantidades
                  {!aCiegas && ', que vienen con lo que falta por recibir'}.
                </span>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
            {pendientes.map((r) => (
              <div key={r.sku} className="px-3 py-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-gray-800 truncate">{r.producto}</div>
                    <div className="text-xs font-mono text-gray-500">
                      {/* En modo ciego NO se enseña cuánto falta. Es la mitad
                          del mecanismo, no un detalle de presentación. */}
                      {r.sku}{aCiegas ? '' : ` · faltan ${Number(r.pendiente)}`}
                    </div>
                  </div>
                  <input
                    type="number" min="0" max={aCiegas ? undefined : Number(r.pendiente)}
                    value={cantidades[r.sku] ?? ''}
                    placeholder={aCiegas ? 'contado' : undefined}
                    aria-label={`Cantidad recibida de ${r.sku}`}
                    // Con el foco aquí, el lector se calla: si no, sus
                    // dígitos entrarían en este campo además de contarse.
                    onFocus={() => setEditando(true)}
                    onBlur={() => setEditando(false)}
                    onChange={(e) => setCantidades((c) => ({ ...c, [r.sku]: e.target.value }))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-right tabular-nums"
                  />
                </div>
                {/* LOS CAMPOS DE CALIDAD SOLO SALEN SI SE VA A RECIBIR ESE
                    RENGLÓN. Una entrega de cuarenta referencias con tres
                    campos más en cada una es un muro; con cantidad en cero
                    ese renglón no viaja, así que sus datos no hacen falta. */}
                {/* DÓNDE ACOMODARLO (INTELIGENCIA I5): solo para el renglón
                    que se va a recibir, y solo si la orden trae el id del
                    producto. Sugiere; la ubicación de arriba sigue mandando. */}
                {Number(cantidades[r.sku] || 0) > 0 && r.id_producto && (
                  <div className="mt-2">
                    <DondeAcomodarlo idProducto={r.id_producto} cantidad={Number(cantidades[r.sku])} />
                  </div>
                )}
                {Number(cantidades[r.sku] || 0) > 0 && (
                  <CamposDeCalidad
                    renglon={r}
                    valores={calidad[r.sku] || {}}
                    exigeLote={exigeLote}
                    subiendo={subiendo === r.sku}
                    onCambio={(campo, valor) => ponerCalidad(r.sku, campo, valor)}
                    onArchivo={(archivo) => subirCertificado(r.sku, archivo)}
                    onEditando={setEditando}
                  />
                )}
              </div>
            ))}
          </div>
          {!aCiegas && (
            <p className="text-xs text-gray-500">
              Aquí no se puede recibir más de lo pedido. Si el proveedor mandó de
              más, cuenta a ciegas: así la diferencia queda registrada contra
              esta orden en vez de perderse en una entrada suelta.
            </p>
          )}

          <AcuseDeEntrega
            idOrden={orden.id}
            entregadoPor={entregadoPor}
            onEntregadoPor={setEntregadoPor}
            token={acuseToken}
            onToken={setAcuseToken}
            onEditando={setEditando}
          />

          <FotosDeLaEntrega
            exigida={avisar}
            faltantes={faltantes}
            ocultarFaltantes={aCiegas}
            huboDano={huboDano}
            onDano={setHuboDano}
            huboRechazo={huboRechazo}
            onRechazo={setHuboRechazo}
            fotos={fotos}
            onAgregar={(f) => setFotos((v) => [...v, f])}
            onQuitar={(i) => setFotos((v) => v.filter((_, n) => n !== i))}
            onNota={(i, nota) => setFotos(
              (v) => v.map((f, n) => (n === i ? { ...f, nota } : f)))}
            onEditando={setEditando}
          />

          {/* PEGADO AL BOTON, que es donde tiene que estar: la regla vive
              junto al control que la ejecuta, no en la nota de arriba.
              `aria-live` porque esto cambia solo mientras se teclea y
              quien no ve la pantalla no se enteraría. */}
          {estorbos.length > 0 && (
            <div aria-live="polite"
                 className="rounded-lg border border-amber-200 bg-amber-50 p-2.5">
              <span className="block text-sm font-semibold text-amber-900">
                Para recibir falta{estorbos.length > 1 ? 'n' : ''} {estorbos.length === 1 ? 'una cosa' : `${estorbos.length} cosas`}
              </span>
              <ul className="mt-1 space-y-1 text-xs text-amber-800 list-disc pl-4">
                {estorbos.map((e) => <li key={e.clave}>{e.texto}</li>)}
              </ul>
            </div>
          )}
        </div>
      }
      etiquetaConfirmar="Recibir"
      onConfirmar={async () => {
        const renglones = pendientes
          .map((r) => {
            const c = calidad[r.sku] || {};
            return {
              sku: r.sku,
              cantidad: Number(cantidades[r.sku] || 0),
              // CUÁNTO SE ESPERABA (Fase 4.2). Es lo que le permite al
              // servidor exigir la foto cuando llegó de menos.
              cantidad_pedida: Number(r.pendiente),
              // Solo se mandan si tienen algo. `undefined` no viaja en el
              // JSON, así que una recepción sin datos de calidad manda
              // exactamente el mismo cuerpo que antes de esta pantalla.
              codigo_lote: c.codigo_lote?.trim() || undefined,
              lote_proveedor: c.lote_proveedor?.trim() || undefined,
              id_certificado: c.id_certificado || undefined,
            };
          })
          .filter((r) => r.cantidad > 0);
        if (!renglones.length) throw new ErrorApi('No indicaste ninguna cantidad.');
        if (!ubicacion) throw new ErrorApi('Elige dónde entra la mercancía.');

        // PREVENIR MEJOR QUE REGAÑAR. Las dos reglas se comprueban también
        // en el servidor —una regla que solo vive en la pantalla se la
        // salta la cola sin señal—, pero decirlo aquí evita el viaje y,
        // sobre todo, evita que la recepción se encole para ser rechazada
        // media hora después, cuando la tarima ya se guardó.
        const sinLoteInterno = renglones.filter(
          (r) => pendientes.find((p) => p.sku === r.sku)?.controla_lote
                 && !r.codigo_lote);
        if (sinLoteInterno.length) {
          throw new ErrorApi(
            `Falta el número de lote en: ${sinLoteInterno.map((r) => r.sku).join(', ')}. `
            + 'Esos productos se manejan por lote, así que sin él no se sabe '
            + 'qué tanda entró. Escríbelo como venga en la caja.');
        }
        // Solo donde el producto maneja lotes, igual que el servidor. Si
        // las dos comprobaciones no dijeran lo mismo, una de las dos
        // estaría de más y la otra sería la que manda sin que se note.
        const sinLoteProveedor = exigeLote
          ? renglones.filter((r) => !r.lote_proveedor
              && pendientes.find((p) => p.sku === r.sku)?.controla_lote)
          : [];
        if (sinLoteProveedor.length) {
          throw new ErrorApi(
            'Tu empresa exige registrar de qué lote del proveedor viene el '
            + 'material de los productos que manejan lotes, y falta en: '
            + `${sinLoteProveedor.map((r) => r.sku).join(', ')}. `
            + 'Escribe el número que trae el certificado del proveedor.');
        }
        // LA FOTO, ANTES DE ENCOLAR NADA. El servidor se niega igual
        // (422), pero sin señal la recepción se quedaría guardada en el
        // teléfono para ser rechazada media hora después — con la tarima
        // ya guardada y el transportista a treinta kilómetros.
        if (necesitaFoto && !fotos.length) {
          setRevelado(true);
          throw new ErrorApi(
            'Esta recepción tiene faltante, daño o rechazo, y no lleva '
            + 'ninguna foto. Sin foto, un faltante es una afirmación que el '
            + 'proveedor puede discutir; con foto y con la hora del '
            + 'servidor, es un documento. Toma al menos una foto de lo que '
            + 'llegó mal y vuelve a guardar.');
        }

        const res = await registrarRecepcion({
          id_orden: orden.id,
          ubicacion,
          renglones,
          a_ciegas: aCiegas,
          clave_idempotencia: claveRef.current,
          // Fase 4. Lo que no se llenó no viaja: `undefined` no se
          // serializa, así que una recepción sin acuse ni fotos manda
          // exactamente el mismo cuerpo que antes de esta pantalla.
          entregado_por: entregadoPor.trim() || undefined,
          acuse_token: acuseToken.trim() || undefined,
          hubo_dano: huboDano || undefined,
          hubo_rechazo: huboRechazo || undefined,
          evidencia: fotos.length ? paraEnviar(fotos) : undefined,
        });

        // SIN SEÑAL NO SE DICE «LISTO». La mercancía está en el andén pero
        // el inventario no se ha movido, y la orden va a seguir enseñando
        // lo mismo que antes. Si esto se callara, el operador leería la
        // orden sin recibir y la recibiría otra vez —con otra clave, que la
        // idempotencia no puede atrapar porque son dos recepciones
        // distintas para el servidor—. Se dice, y la lista lo marca.
        if (res.estado === 'encolado') {
          avisos.aviso(
            'Sin conexión: la recepción quedó guardada en el teléfono y se '
            + 'aplicará al recuperar señal. El inventario todavía NO se ha '
            + 'movido, así que no vuelvas a recibir esta orden.',
          );
          onListo();
          return;
        }
        const r = res.orden;

        // LO QUE QUEDÓ REGISTRADO SE DICE, y se dice en pasado: es la
        // constancia que después sostiene una reclamación, y quien
        // acaba de tomar la foto tiene que ver que llegó.
        const constancia = [
          fotos.length ? `${fotos.length} foto(s)` : null,
          acuseToken.trim() ? 'el código de acuse' : null,
          entregadoPor.trim() ? `entregó ${entregadoPor.trim()}` : null,
        ].filter(Boolean);
        if (constancia.length) {
          avisos.exito(`Quedó constancia de la entrega: ${constancia.join(', ')}.`);
        }

        // La reconciliación se DICE. Contar a ciegas y no enterarse de en
        // qué no cuadró deja el trabajo a medias.
        if (aCiegas) {
          const sobra = r.renglones.filter((x) => Number(x.cantidad_excedente) > 0);
          const falta = r.renglones.filter((x) => Number(x.pendiente) > 0);
          if (sobra.length || falta.length) {
            avisos.aviso(
              'Recepción registrada con diferencias. '
              + (sobra.length
                ? `De más: ${sobra.map((x) => `${x.sku} +${Number(x.cantidad_excedente)}`).join(', ')}. `
                : '')
              + (falta.length
                ? `Pendiente: ${falta.map((x) => `${x.sku} ${Number(x.pendiente)}`).join(', ')}.`
                : ''),
            );
          } else {
            avisos.exito('Recepción a ciegas: todo cuadró con lo pedido.');
          }
        }
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

// LOS TRES CAMPOS DE CALIDAD DE UN RENGLÓN · CAL-2
//
// EXPORTADO PARA PODERLO PROBAR. El diálogo entero necesita sesión, DOM y
// escáner; esto es lo que existe para DECIR algo —qué es obligatorio y por
// qué— y se puede pintar tal cual.
//
// TRES REGLAS DE USUARIO CERO GOBIERNAN ESTE BLOQUE:
//
//   1. **El lote nuestro solo aparece si el producto los maneja.** Pedirlo
//      donde no aplica es un campo que nadie sabe qué poner — y hasta la
//      Fase 1 pasaba lo contrario: no se pedía nunca, así que recibir un
//      producto con control de lote desde esta pantalla FALLABA con «el
//      producto exige indicar el lote» y no había dónde escribirlo.
//   2. **Lo obligatorio se marca, y se dice por qué.** Nada de un asterisco
//      que hay que adivinar.
//   3. **El certificado es opcional siempre**, y se dice: llega días
//      después muchas veces, y exigirlo al recibir significaría que nadie
//      registra el lote.
export function CamposDeCalidad({
  renglon, valores, exigeLote, subiendo, onCambio, onArchivo, onEditando,
}) {
  const faltaLote = renglon.controla_lote && !(valores.codigo_lote || '').trim();
  // EL LOTE DEL PROVEEDOR SIGUE LITERALMENTE A LA FRASE DEL INTERRUPTOR:
  // «al recibir productos que manejan lotes, el lote del proveedor será
  // obligatorio». En un producto sin lotes no hay genealogía que
  // construir, así que exigirlo ahí sería pedir un dato que no apunta a
  // nada — y quien recibe tornillos a granel no tiene por qué saber que
  // ese producto no lleva lote.
  const exigidoAqui = exigeLote && renglon.controla_lote;
  const faltaProveedor = exigidoAqui && !(valores.lote_proveedor || '').trim();

  return (
    <div className="mt-2 pl-1 border-l-2 border-gray-200 space-y-2">
      {renglon.controla_lote && (
        <label className="block">
          <span className="block text-xs font-semibold text-gray-700">
            Lote de esta tanda <span className="text-red-700">· obligatorio</span>
          </span>
          <span className="block text-xs text-gray-500 mb-1">
            Este producto se maneja por lote: sin él no se sabe qué tanda entró.
            Escríbelo como venga en la caja.
          </span>
          <input
            value={valores.codigo_lote || ''}
            aria-label={`Lote de ${renglon.sku}`}
            onFocus={() => onEditando(true)}
            onBlur={() => onEditando(false)}
            onChange={(e) => onCambio('codigo_lote', e.target.value)}
            className={`w-full px-2 py-1 border rounded text-sm ${
              faltaLote ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
          />
        </label>
      )}

      <label className="block">
        <span className="block text-xs font-semibold text-gray-700">
          Lote del proveedor{' '}
          {/* Y SE DICE POR QUÉ, renglón por renglón. Dos renglones de la
              misma entrega con exigencias distintas y sin explicación se
              leen como un capricho del sistema; con el motivo al lado se
              leen como lo que son. */}
          {exigeLote
            ? (renglon.controla_lote
                ? <span className="text-red-700">· obligatorio · este producto maneja lotes</span>
                : <span className="text-gray-400">· opcional · este producto no maneja lotes</span>)
            : <span className="text-gray-400">· opcional</span>}
        </span>
        <span className="block text-xs text-gray-500 mb-1">
          El número que trae su certificado, tal cual. Es lo que permite
          contestar «¿de qué colada venía?» cuando un cliente reclame.
        </span>
        <input
          value={valores.lote_proveedor || ''}
          aria-label={`Lote del proveedor de ${renglon.sku}`}
          onFocus={() => onEditando(true)}
          onBlur={() => onEditando(false)}
          onChange={(e) => onCambio('lote_proveedor', e.target.value)}
          className={`w-full px-2 py-1 border rounded text-sm ${
            faltaProveedor ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
        />
      </label>

      <div>
        <span className="block text-xs font-semibold text-gray-700">
          Certificado de material <span className="text-gray-400">· opcional</span>
        </span>
        <span className="block text-xs text-gray-500 mb-1">
          El PDF o una foto del documento. Si no llegó, recibe igual
          y adjúntalo después.
        </span>
        {valores.certificado ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-green-800 bg-green-50 border border-green-200 rounded px-2 py-1">
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            {valores.certificado}
          </span>
        ) : (
          <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--color-marca)] cursor-pointer">
            <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
            {subiendo ? 'Subiendo…' : 'Adjuntar certificado'}
            <input
              type="file" className="sr-only"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => { onArchivo(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// EL ACUSE DE QUIEN ENTREGA · Fase 4.1 de DIRECTRIZ-VENTAJA
// =====================================================================
//
// EXPORTADO PARA PODERLO PROBAR, igual que `CamposDeCalidad`.
//
// QUÉ PRUEBA UN ACUSE, Y QUÉ NO. El código sale del servidor, con la
// hora del servidor, y se quema al usarse dentro de su media hora. Lo
// que queda demostrado es **que alguien pidió un código en el andén y
// que se usó ahí mismo, dentro de esa media hora**. El nombre lo pone
// quien entrega, y vale lo que vale un nombre dicho: por eso la pantalla
// dice «dicho por quien entrega» y no «verificado».
//
// Esa distinción no es un matiz. Un acuse que la pantalla presentara
// como identidad comprobada sería un icono verde que miente, y el día
// que el proveedor lo discutiera se caería entero — junto con todo lo
// demás que sí se sostiene.
//
// LAS DOS FORMAS SON OPCIONALES, y esa es la mitad del mecanismo: si el
// transportista se va con prisa, la recepción se registra igual y queda
// escrito que no hubo acuse. Exigirlo convertiría la falta de acuse en
// una razón para no registrar la entrega, que es exactamente el agujero
// que esta fase viene a tapar.
export function AcuseDeEntrega({
  idOrden, entregadoPor, onEntregadoPor, token, onToken, onEditando,
}) {
  const [vale, setVale] = useState(null);      // {codigo, minutos, desde}
  const [pidiendo, setPidiendo] = useState(false);
  const [error, setError] = useState(null);
  const [transcurrido, setTranscurrido] = useState(0);

  // EL RELOJ MIDE LO TRANSCURRIDO, NO LA HORA. Un `Date.now()` comparado
  // contra una hora del servidor mentiría en cuanto el teléfono tuviera
  // el reloj corrido —que es la mitad del motivo de la Fase 4.3—; medir
  // cuánto ha pasado desde que se pidió el código es exacto aunque el
  // reloj esté mal puesto.
  useEffect(() => {
    if (!vale) return undefined;
    const t = setInterval(
      () => setTranscurrido(Math.floor((Date.now() - vale.desde) / 60000)),
      10000);
    return () => clearInterval(t);
  }, [vale]);

  const restan = vale ? vale.minutos - transcurrido : 0;

  async function pedir() {
    setPidiendo(true);
    setError(null);
    try {
      const r = await api.post(`/compras/ordenes/${idOrden}/acuse-vale`, {});
      setVale({ codigo: r.token, minutos: r.minutos, desde: Date.now() });
      setTranscurrido(0);
    } catch (e) {
      // SE DICE Y NO SE ROMPE. El acuse es opcional: que el servidor no
      // conteste no puede impedir meter al almacén una mercancía que ya
      // está en el andén.
      setError(e instanceof ErrorApi ? e.message
        : 'El servidor no contestó. Escribe el nombre de quien entrega y '
          + 'recibe igual.');
    } finally {
      setPidiendo(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-3">
      <div>
        <span className="block text-sm font-semibold text-gray-900">
          Quién entregó <span className="text-gray-400 font-normal">· opcional</span>
        </span>
        <Ayuda id="recepcion.acuse" />
      </div>

      <label className="block">
        <span className="block text-xs font-semibold text-gray-700 mb-1">
          Nombre de quien trae la mercancía
        </span>
        <input
          value={entregadoPor}
          maxLength={120}
          placeholder="Transportes del Norte"
          aria-label="Nombre de quien entrega"
          onFocus={() => onEditando(true)}
          onBlur={() => onEditando(false)}
          onChange={(e) => onEntregadoPor(e.target.value)}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
        />
        <Ayuda id="recepcion.entregado_por" />
      </label>

      {!vale ? (
        <div>
          <button
            type="button" onClick={pedir} disabled={pidiendo}
            className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-lg border border-[var(--color-marca)] text-[var(--color-marca)] hover:bg-[var(--color-marca-suave)] disabled:opacity-50"
          >
            <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
            {pidiendo ? 'Pidiendo el código…' : 'Pedir código de acuse'}
          </button>
          <Ayuda id="recepcion.pedir_codigo" />
          {error && (
            <p role="alert" className="text-xs text-red-700 mt-1">{error}</p>
          )}
        </div>
      ) : (
        <ValeDeAcuse
          codigo={vale.codigo} restan={restan} token={token}
          onToken={onToken} onEditando={onEditando}
          onOtro={pedir} pidiendo={pidiendo}
        />
      )}
    </div>
  );
}

// EL VALE EN PANTALLA, APARTE Y EXPORTADO. Es lo que existe para DECIR
// algo —cuánto le queda al código y qué hacer cuando ya no le queda—, y
// separarlo permite probar esas dos frases sin esperar media hora a que
// un reloj de verdad avance.
export function ValeDeAcuse({
  codigo, restan, token, onToken, onEditando, onOtro, pidiendo = false,
}) {
  const caducado = restan <= 0;
  return (
    <div className="rounded-lg border border-[var(--color-marca-borde)] bg-[var(--color-marca-suave)] p-3">
      <span className="block text-xs font-semibold text-gray-700 mb-2">
        Enséñale esto a quien entrega
      </span>
      <div className="flex items-center gap-3">
        <CodigoQR valor={codigo} tamano={96}
                  etiqueta={`Código de acuse ${codigo}`} />
        <div className="min-w-0">
          <span className="block font-mono text-lg font-bold tracking-widest text-gray-900 break-all">
            {codigo}
          </span>
          {/* NUNCA UN CONTADOR QUE MIENTE: cuando el vale caduca, lo dice
              y ofrece el siguiente paso, en vez de seguir enseñando un
              código que el servidor ya rechaza con un 410. */}
          {caducado ? (
            <span className="block text-xs font-semibold text-red-700 mt-1">
              Este código caducó. Pide otro para acusar la entrega.
            </span>
          ) : (
            <span className="block text-xs text-gray-600 mt-1">
              Vale {restan} minuto(s) más, y una sola vez.
            </span>
          )}
          <button
            type="button" onClick={onOtro} disabled={pidiendo}
            className="text-xs font-semibold text-[var(--color-marca)] underline mt-1"
          >
            Pedir otro código
          </button>
        </div>
      </div>

      <label className="block mt-3">
        <span className="block text-xs font-semibold text-gray-700 mb-1">
          Código que tecleó o escaneó quien entrega
        </span>
        {/* NO SE RELLENA SOLO CON EL CÓDIGO QUE ACABA DE SALIR, y es la
            pieza que sostiene todo lo demás: un campo prellenado
            convertiría «alguien acusó» en «la pantalla se acusó a sí
            misma», y el acuse dejaría de probar nada el día que el
            proveedor lo discuta. */}
        <input
          value={token}
          maxLength={64}
          aria-label="Código de acuse"
          onFocus={() => onEditando(true)}
          onBlur={() => onEditando(false)}
          onChange={(e) => onToken(e.target.value.trim())}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm font-mono"
        />
        <Ayuda id="recepcion.codigo_acuse" />
      </label>
    </div>
  );
}

// =====================================================================
// LA FOTO DE LO QUE LLEGÓ MAL · Fase 4.2
// =====================================================================
//
// LA CÁMARA ES LA DEL TELÉFONO, NO UNA DENTRO DE LA PÁGINA. El porqué
// está entero en `lib/evidencia.js`, y se resume en que `getUserMedia`
// exige HTTPS y la consola de planta se sirve por http en la red local:
// una cámara en vivo fallaría justo en el almacén, que es el único sitio
// donde esta pantalla se usa.
//
// SE OFRECE SIEMPRE Y SE EXIGE SOLO CUANDO HAY ALGO QUE ENSEÑAR. Pedir
// foto en una recepción que llegó completa la convertiría en un trámite,
// y un trámite se acaba rellenando con una foto del techo.
export function FotosDeLaEntrega({
  exigida, faltantes, huboDano, huboRechazo, onDano, onRechazo,
  fotos, onAgregar, onQuitar, onNota, onEditando, ocultarFaltantes = false,
}) {
  const [motivo, setMotivo] = useState('FALTANTE');
  const [leyendo, setLeyendo] = useState(false);
  const [error, setError] = useState(null);

  async function tomar(archivo) {
    if (!archivo) return;
    setLeyendo(true);
    setError(null);
    try {
      onAgregar(await fotoDesdeArchivo(archivo, motivo));
    } catch (e) {
      setError(e.message);
    } finally {
      setLeyendo(false);
    }
  }

  const falta = exigida && fotos.length === 0;

  return (
    <div className={`rounded-lg border p-3 space-y-3 ${
      falta ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
      <div>
        <span className="block text-sm font-semibold text-gray-900">
          Fotos de la entrega{' '}
          {exigida
            ? <span className="text-red-700">· obligatoria</span>
            : <span className="text-gray-400 font-normal">· opcional</span>}
        </span>
        <Ayuda id="recepcion.fotos" />
      </div>

      {/* EL DAÑO Y EL RECHAZO SE DECLARAN; EL FALTANTE SALE DE LAS
          CANTIDADES. Una caja mojada que trae la pieza completa dentro no
          la delata ningún número. */}
      <div className="space-y-2">
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={huboDano} className="mt-0.5"
                 onChange={(e) => onDano(e.target.checked)} />
          <span className="text-sm text-gray-800">Llegó material dañado</span>
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" checked={huboRechazo} className="mt-0.5"
                 onChange={(e) => onRechazo(e.target.checked)} />
          <span className="text-sm text-gray-800">Rechacé material en el andén</span>
        </label>
      </div>

      {/* PREVENIR MEJOR QUE REGAÑAR: el hueco se dice mientras se captura,
          con SKU y cantidad, y no al intentar guardar. En conteo a ciegas
          se calla —enseñar aquí lo que falta sería enseñar lo esperado, que
          es justo lo que el conteo a ciegas viene a tapar. */}
      {falta && (
        <p role="alert" className="text-sm text-red-800">
          <span className="block font-semibold">
            Esta recepción no se guarda sin al menos una foto
          </span>
          <span className="block text-xs mt-0.5">
            {!ocultarFaltantes && faltantes.length > 0 && (
              <>Falta material: {faltantes.map(
                (f) => `${f.sku} (${f.falta})`).join(', ')}. </>
            )}
            Un faltante escrito lo puede discutir el proveedor; con foto y
            con la hora del servidor, es un documento. Toma una foto de lo
            que llegó mal.
          </span>
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <label className="block">
          <span className="sr-only">Qué documenta la siguiente foto</span>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  aria-label="Qué documenta la siguiente foto"
                  className="px-2 py-2 border border-gray-300 rounded-lg text-sm bg-white">
            {MOTIVOS.map((m) => (
              <option key={m.clave} value={m.clave}>{m.nombre}</option>
            ))}
          </select>
        </label>
        {/* OBJETIVO TÁCTIL GRANDE Y CON TEXTO: esto se pulsa con guantes,
            con una mano y con el teléfono en la otra. */}
        <label className="inline-flex items-center gap-2 min-h-11 text-sm font-semibold px-4 py-2.5 rounded-lg border border-[var(--color-marca)] text-[var(--color-marca)] cursor-pointer hover:bg-[var(--color-marca-suave)]">
          <Camera className="h-4 w-4" aria-hidden="true" />
          {leyendo ? 'Guardando la foto…' : 'Tomar foto'}
          <input
            type="file" className="sr-only"
            accept="image/*"
            // LA CÁMARA TRASERA, QUE ES LA QUE MIRA A LA TARIMA. Sin esto
            // varios teléfonos abren la galería, y una foto de la galería
            // no es una foto de esta entrega.
            capture="environment"
            aria-label="Tomar foto de lo que llegó mal"
            onChange={(e) => { tomar(e.target.files?.[0]); e.target.value = ''; }}
          />
        </label>
      </div>

      <Ayuda id="recepcion.foto_medida" />

      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}

      {fotos.length > 0 && (
        <ul className="space-y-2">
          {fotos.map((f, i) => (
            <li key={`${f.nombre}-${i}`}
                className="flex items-start gap-2 border border-gray-200 rounded-lg p-2 bg-white">
              <img src={`data:${f._tipo};base64,${f.contenido}`} alt=""
                   className="h-14 w-14 object-cover rounded shrink-0" />
              <div className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-gray-800">
                  {nombreDeMotivo(f.motivo)}
                  <span className="font-normal text-gray-500">
                    {' · '}{Math.max(1, Math.round(f._bytes / 1024))} KB
                  </span>
                </span>
                <input
                  value={f.nota || ''}
                  maxLength={240}
                  placeholder="Qué se ve en la foto (opcional)"
                  aria-label={`Nota de la foto ${i + 1}`}
                  onFocus={() => onEditando(true)}
                  onBlur={() => onEditando(false)}
                  onChange={(e) => onNota(i, e.target.value)}
                  className="w-full mt-1 px-2 py-1 border border-gray-300 rounded text-xs"
                />
              </div>
              {/* QUITAR VALE HASTA QUE SE GUARDA, Y NI UN MINUTO MÁS.
                  Antes de guardar, una foto movida es una foto movida y
                  borrarla es corregir un error de captura. Después, la
                  aplicación no tiene permiso para borrarla ni aunque
                  alguien lo pida: lo dice la migración 162 y lo comprueba
                  ella misma al aplicarse. */}
              <button type="button" onClick={() => onQuitar(i)}
                      className="text-xs font-semibold text-red-700 underline shrink-0">
                Quitar
              </button>
            </li>
          ))}
          <li>
            <Ayuda id="recepcion.foto_no_se_borra" />
          </li>
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------- proveedores
function PanelProveedores() {
  const { tieneNivel } = useAuth();
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);
  const [vincular, setVincular] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    return api.get('/compras/proveedores?solo_activos=false')
      .then(setFilas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vivo = true;
    api.get('/compras/proveedores?solo_activos=false')
      .then((d) => vivo && setFilas(d))
      .catch((e) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, []);

  const columnas = [
    { etiqueta: 'Proveedor', valor: (p) => p.nombre, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Contacto', valor: (p) => p.contacto || '', clase: 'text-gray-500' },
    { etiqueta: 'Teléfono', valor: (p) => p.telefono || '', clase: 'text-gray-500' },
    {
      etiqueta: 'Entrega', alineado: 'derecha', valor: (p) => p.dias_entrega,
      render: (p) => <span className="tabular-nums">{p.dias_entrega} día(s)</span>,
    },
    { etiqueta: 'Productos', alineado: 'derecha', valor: (p) => p.productos },
    {
      etiqueta: 'Estado', valor: (p) => (p.activo ? 'Activo' : 'Dado de baja'),
      render: (p) => (
        <ChipEstado
          mapa={{ activo: { etiqueta: 'Activo', color: 'verde' },
                  baja: { etiqueta: 'Dado de baja', color: 'gris' } }}
          clave={p.activo ? 'activo' : 'baja'}
        />
      ),
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Proveedores"
        subtitulo="A quién se le compra cada cosa, en cuántos días entrega y en qué múltiplo."
        nota={
          <NotaExplicativa titulo="Los días de entrega no son un dato de adorno">
            Son lo que convierte «me quedan 5» en «pide hoy o te quedas sin nada
            el jueves»: la cantidad sugerida incluye lo que se va a consumir
            mientras el pedido está en camino.
          </NotaExplicativa>
        }
        accionPrincipal={tieneNivel('ENCARGADO')
          ? { etiqueta: 'Nuevo proveedor', onClick: () => setEditar({ modo: 'crear' }) }
          : undefined}
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'proveedores', columnas }}
        vacio={
          <EstadoVacio
            icono={TrendingDown}
            titulo="Todavía no hay proveedores"
            descripcion="Sin proveedor, un producto bajo su punto de reorden aparece en la lista pero no se puede agrupar en una orden."
          />
        }
        acciones={(p) => (tieneNivel('ENCARGADO') ? [
          { etiqueta: 'Editar', onClick: () => setEditar({ modo: 'editar', proveedor: p }) },
          { etiqueta: 'Asignar producto', onClick: () => setVincular(p) },
          ...(p.activo
            ? [{ etiqueta: 'Dar de baja', peligro: true,
                 onClick: () => api.patch(`/compras/proveedores/${p.id}`, { activo: false })
                   .then(cargar).catch((e) => setError(e.message)) }]
            : [{ etiqueta: 'Reactivar',
                 onClick: () => api.patch(`/compras/proveedores/${p.id}`, { activo: true })
                   .then(cargar).catch((e) => setError(e.message)) }]),
        ] : [])}
      />

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {editar && (
        <ModalProveedor {...editar} onCerrar={() => setEditar(null)}
                        onListo={() => { setEditar(null); cargar(); }} />
      )}

      {vincular && (
        <ModalVincular proveedor={vincular} onCerrar={() => setVincular(null)}
                       onListo={() => { setVincular(null); cargar(); }} />
      )}
    </>
  );
}

function ModalProveedor({ modo, proveedor, onCerrar, onListo }) {
  const crear = modo === 'crear';
  const [f, setF] = useState({
    nombre: proveedor?.nombre || '',
    contacto: proveedor?.contacto || '',
    telefono: proveedor?.telefono || '',
    email: proveedor?.email || '',
    rfc: proveedor?.rfc || '',
    dias_entrega: proveedor?.dias_entrega ?? 7,
    condiciones: proveedor?.condiciones || '',
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <ModalConfirmar
      titulo={crear ? 'Nuevo proveedor' : `Editar ${proveedor.nombre}`}
      extra={
        <div className="space-y-5">
          <Seccion titulo="Quién surte" columnas={1}>
            <CampoValidado etiqueta="Nombre" requerido valor={f.nombre}
                           validar={(v) => (v.trim() ? null : 'El nombre es obligatorio.')}>
              {(props) => <input {...props} value={f.nombre} onChange={set('nombre')} />}
            </CampoValidado>
          </Seccion>

          <Seccion titulo="Cómo se le localiza"
                   descripcion="Todo opcional, pero sin teléfono ni correo la orden de compra no se puede mandar a ninguna parte.">
            <CampoValidado etiqueta="Contacto" valor={f.contacto}>
              {(props) => <input {...props} value={f.contacto} onChange={set('contacto')} />}
            </CampoValidado>
            <CampoValidado etiqueta="Teléfono" valor={f.telefono}
                           validar={(v) => (!v.trim() || /^\+?\d{8,15}$/.test(v.replace(/[\s()-]/g, ''))
                             ? null : 'Teléfono inválido: de 8 a 15 dígitos.')}>
              {(props) => <input {...props} value={f.telefono} onChange={set('telefono')} />}
            </CampoValidado>
            <CampoValidado etiqueta="Correo" valor={f.email}
                           validar={(v) => (!v.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())
                             ? null : 'Correo inválido.')}>
              {(props) => <input {...props} type="email" value={f.email} onChange={set('email')} />}
            </CampoValidado>
            <CampoValidado etiqueta="RFC" valor={f.rfc}>
              {(props) => <input {...props} value={f.rfc} onChange={set('rfc')}
                                 className={`${props.className} font-mono`} />}
            </CampoValidado>
          </Seccion>

          <Seccion titulo="Condiciones"
                   descripcion="Los días de entrega entran en el cálculo del punto de reorden: si están mal, el sistema pide tarde.">
            <CampoValidado etiqueta="Días de entrega" valor={f.dias_entrega}
                           validar={(v) => (Number(v) >= 0 ? null : 'No pueden ser negativos.')}>
              {(props) => <input {...props} type="number" min="0" value={f.dias_entrega}
                                 onChange={set('dias_entrega')}
                                 className={`${props.className} tabular-nums`} />}
            </CampoValidado>
            <CampoValidado etiqueta="Condiciones de pago" valor={f.condiciones}>
              {(props) => <input {...props} value={f.condiciones} onChange={set('condiciones')}
                                 placeholder="30 días, contado…" />}
            </CampoValidado>
          </Seccion>
        </div>
      }
      etiquetaConfirmar={crear ? 'Crear proveedor' : 'Guardar'}
      onConfirmar={async () => {
        if (!f.nombre.trim()) throw new ErrorApi('El nombre es obligatorio.');
        const cuerpo = {
          nombre: f.nombre.trim(),
          dias_entrega: Number(f.dias_entrega),
          ...Object.fromEntries(['contacto', 'telefono', 'email', 'rfc', 'condiciones']
            .filter((k) => f[k].trim()).map((k) => [k, f[k].trim()])),
        };
        if (crear) await api.post('/compras/proveedores', cuerpo);
        else await api.patch(`/compras/proveedores/${proveedor.id}`, cuerpo);
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

function ModalVincular({ proveedor, onCerrar, onListo }) {
  const [f, setF] = useState({
    sku: '', precio: '', multiplo_compra: '1', codigo_proveedor: '', principal: true,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <ModalConfirmar
      titulo={`Asignar un producto a ${proveedor.nombre}`}
      descripcion="Quién surte este producto, a qué precio y en qué múltiplo se compra."
      extra={
        <div className="space-y-5">
          <Seccion titulo="Qué producto" columnas={1}>
            <CampoValidado etiqueta="SKU" requerido valor={f.sku}
                           validar={(v) => (v.trim() ? null : 'Indica el SKU.')}>
              {(props) => <input {...props} value={f.sku} onChange={set('sku')}
                                 className={`${props.className} font-mono`}
                                 autoCapitalize="characters" />}
            </CampoValidado>
          </Seccion>

          <Seccion titulo="A qué precio y en qué cantidad"
                   descripcion="El múltiplo importa más de lo que parece: no se compran 7 si vienen en cajas de 12, y sin ese dato la sugerencia produce órdenes que el proveedor no puede surtir.">
            <CampoValidado etiqueta="Precio de compra" valor={f.precio}
                           validar={(v) => (!String(v).trim() || Number(v) >= 0
                             ? null : 'El precio no puede ser negativo.')}>
              {(props) => <input {...props} value={f.precio} onChange={set('precio')}
                                 inputMode="decimal"
                                 className={`${props.className} tabular-nums`} />}
            </CampoValidado>
            <CampoValidado etiqueta="Múltiplo de compra" valor={f.multiplo_compra}
                           validar={(v) => (Number(v) > 0 ? null : 'Tiene que ser mayor que cero.')}>
              {(props) => <input {...props} value={f.multiplo_compra}
                                 onChange={set('multiplo_compra')} inputMode="decimal"
                                 className={`${props.className} tabular-nums`} />}
            </CampoValidado>
            <CampoValidado etiqueta="Código en el proveedor" ayuda="Opcional."
                           ancho="completo" valor={f.codigo_proveedor}>
              {(props) => <input {...props} value={f.codigo_proveedor}
                                 onChange={set('codigo_proveedor')}
                                 className={`${props.className} font-mono`} />}
            </CampoValidado>
          </Seccion>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={f.principal}
                   onChange={(e) => setF((s) => ({ ...s, principal: e.target.checked }))} />
            Es el proveedor principal de este producto
          </label>
        </div>
      }
      etiquetaConfirmar="Asignar"
      onConfirmar={async () => {
        if (!f.sku.trim()) throw new ErrorApi('Indica el SKU.');
        await api.post(`/compras/productos/${f.sku.trim().toUpperCase()}/proveedor`, {
          id_proveedor: proveedor.id,
          precio: f.precio ? String(f.precio) : null,
          multiplo_compra: String(f.multiplo_compra || 1),
          codigo_proveedor: f.codigo_proveedor.trim() || null,
          principal: f.principal,
        });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}



// SE PREGUNTA DE QUIEN FUE, Y EN EL MOMENTO. Hasta la migración 161 no
// se registraba, y la evaluación de proveedor tenía que contar TODAS las
// canceladas como neutras — la pantalla llegaba a decir «casi siempre se
// cancelan por razones nuestras», que era una confesión escrita sobre un
// dato que no existía.
//
// Se pregunta AQUÍ y no en un repaso posterior: dentro de un mes nadie
// se acuerda, y una casilla que se rellena de memoria es peor que no
// tenerla — parecería un dato y no lo sería.
const ORIGENES = [
  ['NOSOTROS',  'Nosotros',            'Cambió el plan, se pidió de más, ya no hace falta.'],
  ['PROVEEDOR', 'El proveedor',        'No pudo surtir, se le acabó, canceló él.'],
  ['ACUERDO',   'De común acuerdo',    'Se habló y los dos decidieron dejarla.'],
];

function ModalCancelar({ orden, onCerrar, onListo }) {
  const [origen, setOrigen] = useState('');
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const falta = !origen
    ? 'Elige de quién fue la cancelación.'
    : (motivo.trim().length < 5 ? 'Escribe el motivo, aunque sea corto.' : null);

  async function confirmar(e) {
    e.preventDefault();
    if (falta) return;
    setEnviando(true);
    try {
      await api.post(`/compras/ordenes/${orden.id}/cancelar`,
                     { motivo: motivo.trim(), origen });
      onListo();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cancelar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal titulo={`Cancelar la orden ${orden.folio}`} onCerrar={onCerrar}>
      <form onSubmit={confirmar} className="flex flex-col gap-4">
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-semibold text-gray-800 mb-1">
            ¿De quién fue?
          </legend>
          {ORIGENES.map(([valor, etiqueta, ejemplo]) => (
            <label key={valor}
                   className={`flex gap-2 items-start rounded-lg border p-2.5 cursor-pointer
                               ${origen === valor
                                 ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]'
                                 : 'border-gray-200'}`}>
              <input type="radio" name="origen" value={valor} className="mt-1"
                     checked={origen === valor}
                     onChange={() => setOrigen(valor)} />
              <span>
                <span className="block text-sm font-medium text-gray-900">{etiqueta}</span>
                <span className="block text-xs text-gray-500">{ejemplo}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Ayuda id="compras.origen_cancelacion" />

        <label className="block">
          <span className="block text-sm font-semibold text-gray-700 mb-1">
            Motivo
          </span>
          <input className="entrada w-full" value={motivo} maxLength={500}
                 onChange={(e) => setMotivo(e.target.value)}
                 placeholder="Se consiguió con otro proveedor" />
        </label>

        {error && (
          <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
            Volver
          </button>
          <button type="submit" disabled={!!falta || enviando}
                  className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold
                             text-white disabled:opacity-50">
            {enviando ? 'Cancelando…' : 'Cancelar orden'}
          </button>
        </div>
        {falta && <p className="text-xs text-amber-800 text-right -mt-2">{falta}</p>}
      </form>
    </Modal>
  );
}


// ------------------------------------------------- por registrar
// LO QUE ANTES ERA UNA COLUMNA EN EL BOLETÍN DEL PROVEEDOR.
//
// «Sin constancia» contaba órdenes confirmadas que nadie registró haber
// mandado. Medía nuestra disciplina y aparecía en su evaluación — y de
// paso le regalaba esas entregas: sin constancia de envío no hay fecha
// comprometida que defender, así que tampoco entraban en su puntualidad.
//
// Aquí es lo que es: trabajo pendiente, con antigüedad y con nombre.
function PanelSinConstancia() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get('/compras/informes/sin-constancia')
      .then((r) => vivo && setFilas(r))
      .catch((e) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, []);

  const columnas = [
    { etiqueta: 'Folio', valor: (f) => f.folio, clase: 'font-mono text-xs' },
    { etiqueta: 'Proveedor', valor: (f) => f.proveedor, clase: 'font-medium text-gray-900' },
    {
      etiqueta: 'Esperando', alineado: 'derecha',
      valor: (f) => f.dias_esperando,
      render: (f) => (
        <span className={`tabular-nums font-semibold ${
          f.dias_esperando >= 14 ? 'text-red-700'
            : f.dias_esperando >= 7 ? 'text-amber-700' : 'text-gray-700'}`}>
          {f.dias_esperando} d
        </span>
      ),
    },
    { etiqueta: 'Renglones', alineado: 'derecha', valor: (f) => f.renglones,
      clase: 'tabular-nums text-gray-600' },
    {
      etiqueta: 'A quién le toca',
      valor: (f) => f.responsable || '',
      render: (f) => (f.responsable
        ? <span className="text-gray-700">{f.responsable}</span>
        : <span className="text-amber-800 text-xs">Sin asignar</span>),
    },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Órdenes por registrar"
        subtitulo="Confirmadas, pero sin constancia de que salieran de aquí. Es trabajo pendiente del almacén, no una nota del proveedor."
        nota={<Ayuda id="compras.sin_constancia" />}
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'ordenes-por-registrar', columnas }}
        vacio={
          <EstadoVacio
            icono={ClipboardCheck}
            titulo="No queda ninguna por registrar"
            descripcion="De todas las órdenes confirmadas consta cómo salieron."
          />
        }
      />
      {error && (
        <div role="alert" className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}
    </>
  );
}
